import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Loader2, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Login() {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { username: "admin", password: "" } });

  if (!loading && isAuthenticated) {
    const dest = location.state?.from?.pathname || "/trunks";
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (values) => {
    setSubmitting(true);
    const res = await login(values.username, values.password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate(location.state?.from?.pathname || "/trunks", { replace: true });
    } else {
      toast.error(res.error || "Invalid credentials");
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background px-4"
      data-testid="login-page"
    >
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2">
          <Waves className="h-5 w-5 text-primary" />
          <div className="text-lg font-semibold tracking-tight">VoxIVR</div>
          <div className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            Admin Console
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Use your admin credentials. The initial password is logged on first
            boot — see <code className="text-xs">test_credentials.md</code>.
          </p>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mt-6 space-y-4"
            data-testid="login-form"
          >
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                autoComplete="username"
                disabled={submitting}
                data-testid="login-username"
                {...register("username", { required: "Username is required" })}
              />
              {errors.username && (
                <p className="text-xs text-destructive">{errors.username.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                disabled={submitting}
                data-testid="login-password"
                {...register("password", { required: "Password is required" })}
              />
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="login-submit"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          VoxIVR — self-hosted AI voice IVR platform
        </p>
      </div>
    </div>
  );
}
